/**
 * Labs (`BACKLOG.md` N7's demo-unblocking half): manual-entry lab results. See
 * `server/src/db/schema/labs.ts`'s header for what this does and does not cover — this is not a real
 * lab interface (no FHIR/HL7v2 connection to any actual lab), and nothing here assumes one exists.
 *
 * **The lab test catalog below is a small curated demo panel, not a licensed compendium.** A real
 * lab-results surface maps to LOINC codes, which (unlike CPT) needs no license but has ~100,000
 * codes and no meaningful "curated subset" a demo could pass off as complete. This is enough breadth
 * to demonstrate the workflow — common CBC/BMP/lipid/endocrine panel components — not a production
 * coding surface, the same call `rcm.ts` already made for CPT/ICD-10.
 */

import { z } from 'zod';

// --- Demo test catalog ---------------------------------------------------------------------------

export type AbnormalFlag = 'normal' | 'low' | 'high' | 'critical';
export const ABNORMAL_FLAGS: readonly AbnormalFlag[] = ['normal', 'low', 'high', 'critical'];

export interface DemoLabTest {
  readonly name: string;
  readonly unit: string;
  /** Display text (e.g. "4.5-11.0"), not a parsed bound — nothing computes against this, it is
   * rendered next to the value exactly as `db/schema/labs.ts`'s own `referenceRange` column is. */
  readonly referenceRange: string;
}

export const DEMO_LAB_TESTS: readonly DemoLabTest[] = [
  { name: 'WBC', unit: 'x10^3/uL', referenceRange: '4.5-11.0' },
  { name: 'Hemoglobin', unit: 'g/dL', referenceRange: '12.0-17.5' },
  { name: 'Platelets', unit: 'x10^3/uL', referenceRange: '150-400' },
  { name: 'Sodium', unit: 'mmol/L', referenceRange: '135-145' },
  { name: 'Potassium', unit: 'mmol/L', referenceRange: '3.5-5.1' },
  { name: 'Creatinine', unit: 'mg/dL', referenceRange: '0.6-1.3' },
  { name: 'Glucose, fasting', unit: 'mg/dL', referenceRange: '70-99' },
  { name: 'Hemoglobin A1c', unit: '%', referenceRange: '4.0-5.6' },
  { name: 'Total cholesterol', unit: 'mg/dL', referenceRange: '<200' },
  { name: 'LDL cholesterol', unit: 'mg/dL', referenceRange: '<100' },
  { name: 'HDL cholesterol', unit: 'mg/dL', referenceRange: '>40' },
  { name: 'TSH', unit: 'mIU/L', referenceRange: '0.4-4.0' },
] as const;

export const DEMO_LAB_TEST_VALUES = DEMO_LAB_TESTS.map((t) => t.name) as [string, ...string[]];

export const findLabTest = (testName: string): DemoLabTest | undefined =>
  DEMO_LAB_TESTS.find((t) => t.name === testName);

// --- Request/response schemas --------------------------------------------------------------------

export const LAB_ERROR_CODES = [
  /** The body failed schema validation, or `testName` is not in the demo catalog. */
  'invalid_request',
  /** The role holds the capability in general, but `evaluateChartAccess` refused this patient. */
  'chart_access_denied',
  /** The role does not hold the capability this route requires at all. */
  'forbidden',
  /** No such lab result, encounter, or patient in the caller's own tenant. */
  'not_found',
] as const;
export type LabErrorCode = (typeof LAB_ERROR_CODES)[number];

const testNameSchema = z.enum(DEMO_LAB_TEST_VALUES);
const abnormalFlagSchema = z.enum(ABNORMAL_FLAGS);
/** A decimal string, matching `rcm.ts`'s own convention — never a float. */
const decimalValueSchema = z
  .string()
  .trim()
  .regex(/^-?\d+(\.\d{1,4})?$/, 'must be a decimal number');

export const createLabResultRequestSchema = z
  .object({
    encounterId: z.uuid().nullable().default(null),
    testName: testNameSchema,
    value: decimalValueSchema,
    abnormalFlag: abnormalFlagSchema.default('normal'),
    resultedAt: z.iso.datetime({ offset: true }),
    confidentialityReasons: z.array(z.string()).default([]),
  })
  .strict();
export type CreateLabResultRequest = z.infer<typeof createLabResultRequestSchema>;

export const labResultResponseSchema = z.object({
  id: z.uuid(),
  patientId: z.uuid(),
  encounterId: z.uuid().nullable(),
  orderedByUserId: z.uuid(),
  testName: z.string(),
  value: z.string(),
  unit: z.string(),
  referenceRange: z.string(),
  abnormalFlag: z.string(),
  resultedAt: z.string(),
  confidentialityReasons: z.array(z.string()),
  createdAt: z.string(),
});
export type LabResultResponse = z.infer<typeof labResultResponseSchema>;

export const labResultEnvelopeSchema = z.object({ labResult: labResultResponseSchema });
export const labResultListEnvelopeSchema = z.object({
  labResults: z.array(labResultResponseSchema),
});
